import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSnackbar } from 'notistack';
import { some } from 'lodash';
import mapboxgl from 'mapbox-gl';

import { useSignalRContext } from '~contexts/SignalRContext';
import { useRequest } from '~hooks/useRequest';
import { useInterval } from '~hooks/useInterval';
import { useClientOnMethod } from '~hooks/useSignalR';
import {
  getItemsInProgress,
  getItemsInProgressByRoute,
  getItemsInProgressByZone,
  getItemsInProgressByCompany,
} from '~services/admin/report';
import {
  AdminReportItemsInProgressMapItem,
  AdminReportItemsInProgressResponse,
  AdminReportItemsInProgressByRouteResponse,
  AdminReportItemsInProgressByZoneResponse,
  AdminReportItemsInProgressByCompanyResponse,
} from '~services/admin/report/types';
import {
  getCoordsPathByRoute,
  getCoordsTrackingByRoute,
} from '~services/route';
import { ApiTransactionVehicle } from '~services/apiTransaction/types';
import { LogisticZoneMap } from '~services/logisticsZones/types';
import {
  Nullable,
  MapCoordinates,
  MapCurrentVehicle,
} from '~globals/types/commons';
import { NotificationVehicle } from '~globals/types/notifications';
import { LogisticZoneType } from '~globals/types/enums';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';
import { METHODS_NAME } from '~constants/signalR';

import { useDashboardVariantV2CarrierContentInTransitTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentInTransitTrackingContextValue,
  DashboardVariantV2CarrierContentInTransitTrackingPanelId,
} from './types';
import {
  getReportItemsInProgressModeByTabId,
  sortTrackingDataResponse,
  parseCoordsTrip,
} from './utils';

const DEFAULT_INTERVAL_RELOAD_DATA = 5000; //@TODO: UI V2: Verify

const DashboardVariantV2CarrierContentInTransitTrackingContext =
  createContext<DashboardVariantV2CarrierContentInTransitTrackingContextValue>({
    activeTab: {
      tabId: 'drivers',
      setTabId: () => {},
    },
    tabsData: {
      data: null,
      isFirstRequest: true,
      loading: true,
      selectedRouteId: {
        value: null,
        setValue: () => {},
      },
      selectedZoneId: {
        value: null,
        setValue: () => {},
      },
      selectedCompany: {
        value: null,
        setValue: () => {},
      },
    },
    mapData: {
      isFirstRequest: true,
      loading: true,
      items: [],
      vehicles: [],
      logisticZones: [],
      currentRouteTrip: [],
      currentRouteTracking: [],
    },
  });

export const useDashboardVariantV2CarrierContentInTransitTrackingContext =
  (): DashboardVariantV2CarrierContentInTransitTrackingContextValue =>
    useContext(DashboardVariantV2CarrierContentInTransitTrackingContext);

const DashboardVariantV2CarrierContentInTransitTrackingProvider: FC = ({
  children,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { hubConnection } = useSignalRContext();

  const {
    isOnlyTodayDelivery: { value: isOnlyTodayDelivery },
  } = useDashboardVariantV2CarrierContentInTransitTabContext();

  const [activeTabId, setActiveTabId] =
    useState<DashboardVariantV2CarrierContentInTransitTrackingPanelId>(
      'drivers',
    );

  const isSelectedDriversTab = useMemo(
    () => activeTabId === 'drivers',
    [activeTabId],
  );
  const isSelectedZonesTab = useMemo(
    () => activeTabId === 'zones',
    [activeTabId],
  );
  const isSelectedCompaniesTab = useMemo(
    () => activeTabId === 'companies',
    [activeTabId],
  );

  const [selectedRouteId, setSelectedRouteId] =
    useState<Nullable<string>>(null);
  const isSelectedRouteIdValid = useMemo(
    () => selectedRouteId !== null && selectedRouteId !== DEFAULT_UUIID_EMPTY,
    [selectedRouteId],
  );
  const [selectedZoneId, setSelectedZoneId] = useState<Nullable<string>>(null);
  const [selectedCompany, setSelectedCompany] =
    useState<Nullable<{ companyId: string; giverCompanyId: string }>>(null);

  const [globalReloadCount, setGlobalReloadCount] = useState(0);

  useInterval(() => {
    setGlobalReloadCount((prevCount) => prevCount + 1);
  }, DEFAULT_INTERVAL_RELOAD_DATA);

  const [isFirstRequestTabData, setIsFirstRequestTabData] = useState(true);
  const [tabsData, setTabsData] =
    useState<Nullable<AdminReportItemsInProgressResponse>>(null);

  const reportItemsInProgressMode = useMemo(
    () => getReportItemsInProgressModeByTabId(activeTabId),
    [activeTabId],
  );

  const [loadingTabData, setLoadingTabData] = useState(true);
  useRequest(
    {
      request: getItemsInProgress,
      payload: {
        reportItemsInProgressMode,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const trackingDataResponse = response.data
          ?.data as AdminReportItemsInProgressResponse;

        const trackingDataResponseSorted =
          sortTrackingDataResponse(trackingDataResponse);

        setTabsData(trackingDataResponseSorted);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestTabData(false);
        setLoadingTabData(false);
      },
    },
    [reportItemsInProgressMode, isOnlyTodayDelivery, globalReloadCount],
  );

  const [isFirstRequestMapData, setIsFirstRequestMapData] = useState(true);
  const [mapItems, setMapItems] = useState<AdminReportItemsInProgressMapItem[]>(
    [],
  );
  const [mapVehicles, setMapVehicles] = useState<ApiTransactionVehicle[]>([]);
  const [mapLogisticZones, setMapLogisticZones] = useState<LogisticZoneMap[]>(
    [],
  );
  const [mapCurrentRouteTrip, setMapCurrentRouteTrip] =
    useState<MapCoordinates>([]);
  const [mapCurrentRouteTracking, setMapCurrentRouteTracking] =
    useState<MapCoordinates>([]);

  const [loadingMapDataByRoute, setLoadingMapDataByRoute] =
    useState(isSelectedDriversTab);
  useRequest(
    {
      request: getItemsInProgressByRoute,
      isValidToRequest: isSelectedDriversTab,
      payload: {
        routeId: selectedRouteId,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const { items, vehicles } = response.data
          ?.data as AdminReportItemsInProgressByRouteResponse;

        const itemsMapped = selectedRouteId ? items : [];

        setMapItems(itemsMapped);
        setMapVehicles(vehicles);
        setMapLogisticZones([]);

        if (selectedRouteId === DEFAULT_UUIID_EMPTY) {
          setMapCurrentRouteTrip([]);
          setMapCurrentRouteTracking([]);
        }

        // Force Reload without items for selectedRouteId
        if (selectedRouteId && itemsMapped.length === 0) {
          setSelectedRouteId(null);
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapDataByRoute(false);
      },
    },
    [activeTabId, selectedRouteId, isOnlyTodayDelivery, globalReloadCount],
  );

  const [loadingMapCurrentRouteTrip, setLoadingMapCurrentRouteTrip] = useState(
    isSelectedRouteIdValid,
  );
  useRequest(
    {
      request: getCoordsPathByRoute,
      isValidToRequest: isSelectedRouteIdValid,
      payload: selectedRouteId as string,
      withPostSuccess: (response) => {
        const responseCurrenRouteTrip = response.data?.data as MapCoordinates;

        const currentTrip = Array.isArray(responseCurrenRouteTrip)
          ? parseCoordsTrip(responseCurrenRouteTrip)
          : [];

        setMapCurrentRouteTrip(currentTrip);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapCurrentRouteTrip(false);
      },
    },
    [selectedRouteId],
  );

  const [
    loadingMapRouteCurrentRouteTracking,
    setLoadingMapRouteCurrentRouteTracking,
  ] = useState(isSelectedRouteIdValid);
  useRequest(
    {
      request: getCoordsTrackingByRoute,
      isValidToRequest: isSelectedRouteIdValid,
      payload: selectedRouteId as string,
      withPostSuccess: (response) => {
        const responseCurrenRouteTracking = response.data
          ?.data as MapCoordinates;

        const currentTracking = Array.isArray(responseCurrenRouteTracking)
          ? parseCoordsTrip(responseCurrenRouteTracking)
          : [];

        setMapCurrentRouteTracking(currentTracking);
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapRouteCurrentRouteTracking(false);
      },
    },
    [selectedRouteId],
  );

  const [loadingMapDataByZone, setLoadingMapDataByZone] =
    useState(isSelectedZonesTab);
  useRequest(
    {
      request: getItemsInProgressByZone,
      isValidToRequest: isSelectedZonesTab,
      payload: {
        zoneId: selectedZoneId,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const { items, zones } = response.data
          ?.data as AdminReportItemsInProgressByZoneResponse;

        const filterZones = zones.filter(
          (zone) => zone.zoneTypeId === LogisticZoneType.GeometryArea,
        );

        setMapItems(items);
        setMapVehicles([]);
        setMapLogisticZones(filterZones);
        setMapCurrentRouteTrip([]);
        setMapCurrentRouteTracking([]);

        // Force Reload without items for selectedZoneId
        if (selectedZoneId && items.length === 0) {
          setSelectedZoneId(null);
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapDataByZone(false);
      },
    },
    [activeTabId, selectedZoneId, isOnlyTodayDelivery, globalReloadCount],
  );

  const [loadingMapDataByCompany, setLoadingMapDataByCompany] = useState(
    isSelectedCompaniesTab,
  );
  useRequest(
    {
      request: getItemsInProgressByCompany,
      isValidToRequest: isSelectedCompaniesTab,
      initialLoading: isSelectedCompaniesTab,
      payload: {
        companyId:
          selectedCompany?.companyId === DEFAULT_UUIID_EMPTY
            ? selectedCompany.giverCompanyId
            : selectedCompany?.companyId ?? null,
        onlyTodayDelivery: isOnlyTodayDelivery,
      },
      withPostSuccess: (response) => {
        const { items } = response.data
          ?.data as AdminReportItemsInProgressByCompanyResponse;

        setMapItems(items);
        setMapVehicles([]);
        setMapLogisticZones([]);
        setMapCurrentRouteTrip([]);
        setMapCurrentRouteTracking([]);

        // Force Reload without items for selectedCompany
        if (selectedCompany && items.length === 0) {
          setSelectedCompany(null);
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo cargar los datos', { variant: 'error' });
      },
      withPostFetch: () => {
        setIsFirstRequestMapData(false);
        setLoadingMapDataByCompany(false);
      },
    },
    [
      activeTabId,
      selectedCompany?.companyId,
      selectedCompany?.giverCompanyId,
      isOnlyTodayDelivery,
      globalReloadCount,
    ],
  );

  const handleChangeActiveTabIdWithLoading = useCallback(
    (newTabId: DashboardVariantV2CarrierContentInTransitTrackingPanelId) => {
      setLoadingTabData(true);

      setActiveTabId(newTabId);

      setSelectedRouteId(null);
      setSelectedZoneId(null);
      setSelectedCompany(null);
    },
    [],
  );

  const handleChangeSelectedRouteId = useCallback(
    (newRouteId: Nullable<string>) => {
      setLoadingMapDataByRoute(true);

      if (newRouteId && newRouteId !== DEFAULT_UUIID_EMPTY) {
        setLoadingMapCurrentRouteTrip(true);
        setLoadingMapRouteCurrentRouteTracking(true);
      }

      setSelectedRouteId(newRouteId);
    },
    [],
  );

  const handleChangeSelectedZoneId = useCallback(
    (newZoneId: Nullable<string>) => {
      setLoadingMapDataByZone(true);

      setSelectedZoneId(newZoneId);
    },
    [],
  );

  const handleChangeSelectedCompany = useCallback(
    (newCompany: Nullable<{ companyId: string; giverCompanyId: string }>) => {
      setLoadingMapDataByCompany(true);

      setSelectedCompany(newCompany);
    },
    [],
  );

  const setCurrentVehicleData = useCallback(
    (vehicleInfo: MapCurrentVehicle) => {
      setMapVehicles((prevMapVehicles) =>
        prevMapVehicles.map((currentVehicle) =>
          currentVehicle.vehicleId === vehicleInfo.vehicleId
            ? { ...currentVehicle, ...vehicleInfo }
            : currentVehicle,
        ),
      );
    },
    [],
  );

  const updateVehicleDataBySocket = useCallback(
    (notification: string) => {
      const infoVehicleNotification = JSON.parse(
        notification,
      ) as NotificationVehicle;

      const isPresentVehicleInData = some(mapVehicles, {
        vehicleId: infoVehicleNotification.VehicleId,
      });

      if (isPresentVehicleInData) {
        setCurrentVehicleData({
          vehicleId: infoVehicleNotification.VehicleId,
          latitude: infoVehicleNotification.Latitude,
          longitude: infoVehicleNotification.Longitude,
          speed: infoVehicleNotification.Speed,
          battery: infoVehicleNotification.Battery,
          lastGeoReferenceDateTime:
            infoVehicleNotification.LastGeoReferenceDateTime,
        });

        if (isSelectedRouteIdValid && mapCurrentRouteTracking.length > 0) {
          setMapCurrentRouteTracking((prevMapCurrentRouteTracking) => [
            ...prevMapCurrentRouteTracking,
            new mapboxgl.LngLat(
              infoVehicleNotification.Longitude,
              infoVehicleNotification.Latitude,
            ).toArray(),
          ]);
        }
      }
    },
    [
      isSelectedRouteIdValid,
      mapCurrentRouteTracking.length,
      mapVehicles,
      setCurrentVehicleData,
    ],
  );

  useClientOnMethod(
    hubConnection,
    METHODS_NAME.REFRESH_VEHICLE,
    updateVehicleDataBySocket,
  );

  const loadingMapData = useMemo(
    () =>
      loadingMapDataByRoute ||
      loadingMapCurrentRouteTrip ||
      loadingMapRouteCurrentRouteTracking ||
      loadingMapDataByZone ||
      loadingMapDataByCompany,
    [
      loadingMapDataByRoute,
      loadingMapCurrentRouteTrip,
      loadingMapRouteCurrentRouteTracking,
      loadingMapDataByZone,
      loadingMapDataByCompany,
    ],
  );

  return (
    <DashboardVariantV2CarrierContentInTransitTrackingContext.Provider
      value={{
        activeTab: {
          tabId: activeTabId,
          setTabId: handleChangeActiveTabIdWithLoading,
        },
        tabsData: {
          data: tabsData,
          isFirstRequest: isFirstRequestTabData,
          loading: loadingTabData,
          selectedRouteId: {
            value: selectedRouteId,
            setValue: handleChangeSelectedRouteId,
          },
          selectedZoneId: {
            value: selectedZoneId,
            setValue: handleChangeSelectedZoneId,
          },
          selectedCompany: {
            value: selectedCompany,
            setValue: handleChangeSelectedCompany,
          },
        },
        mapData: {
          isFirstRequest: isFirstRequestMapData,
          loading: loadingMapData,
          items: mapItems,
          vehicles: mapVehicles,
          logisticZones: mapLogisticZones,
          currentRouteTrip: mapCurrentRouteTrip,
          currentRouteTracking: mapCurrentRouteTracking,
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContentInTransitTrackingContext.Provider>
  );
};

export default DashboardVariantV2CarrierContentInTransitTrackingProvider;
